import type { ExpandedItems } from '../../../../../../model/table';
import { ITEM_TYPE, CHILD_ITEM_TYPE } from './constants';
import type { GenericItem, GenericChildItem, EnrichedItem, EnrichedChildItem } from './types';

const enrichChildItem = <
	ChildItemType extends GenericChildItem,
	ItemType extends GenericItem<ChildItemType>,
>(
	item: ChildItemType,
	parentItem: ItemType,
	rootIndex: number,
	flatIndex: number,
): EnrichedChildItem<ChildItemType> => ({
	type: CHILD_ITEM_TYPE,
	item,
	meta: {
		parentId: parentItem.id,
		isParent: false,
		isExpanded: false,
	},
	flatIndex,
});

const enrichItem = <
	ChildItemType extends GenericChildItem,
	ItemType extends GenericItem<ChildItemType>,
>(
	item: ItemType,
	childItems: EnrichedChildItem<ChildItemType>[],
	expandedItems: ExpandedItems,
	rootIndex: number,
	flatIndex: number,
): EnrichedItem<ChildItemType, ItemType> => ({
	type: ITEM_TYPE,
	item,
	childItems,
	meta: {
		parentId: undefined,
		isParent: item.childItems.length > 0,
		isExpanded: Boolean(expandedItems[item.id]),
	},
	flatIndex,
});

/* Enrich the item tree to prepare it for rendering:
 * - Construct meta items to preserve hierarchy information (items will be flattened during rendering)
 * - Transform the creation anchor into a first-class citizen item
 * - Determine the visibility of child items
 */
export const getHierarchyEnrichedItems = <
	ChildItemType extends GenericChildItem,
	ItemType extends GenericItem<ChildItemType>,
>(
	items: ItemType[],
	expandedItems: ExpandedItems,
	_activeLevel: number,
): EnrichedItem<ChildItemType, ItemType>[] => {
	const enrichedItems: EnrichedItem<ChildItemType, ItemType>[] = [];

	// Represents the position of an item if the tree was flattened
	let flatIndex = 0;

	items.forEach((item, rootIndex) => {
		const { id, childItems } = item;

		const flatParentIndex = flatIndex;
		const enrichedChildItems: Array<EnrichedChildItem<ChildItemType>> = [];

		/* Increase the index to account for the parent item, which will inserted first,
		 * but requires the child items to be generated beforehand.
		 */
		flatIndex += 1;

		// Child items are only visible if their parent has been expanded
		if (expandedItems[id]) {
			childItems.forEach((childItem) => {
				enrichedChildItems.push(enrichChildItem(childItem, item, rootIndex, flatIndex));
				flatIndex += 1;
			});
		}

		const enrichedItem: EnrichedItem<ChildItemType, ItemType> = enrichItem<ChildItemType, ItemType>(
			item,
			enrichedChildItems,
			expandedItems,
			rootIndex,
			flatParentIndex,
		);
		enrichedItems.push(enrichedItem);
	});

	return enrichedItems;
};
